import {
  getLeoUuid,
  LeoUUID,
  getString,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class ValidateCSVAndUnmapRespondentsFromSpocsRPC implements
  LeoRPC<ValidateCSVAndUnmapRespondentsFromSpocsRPC.Request, ValidateCSVAndUnmapRespondentsFromSpocsRPC.Response, ValidateCSVAndUnmapRespondentsFromSpocsRPC.Errors.Errors>
{
  abstract execute(
    request: ValidateCSVAndUnmapRespondentsFromSpocsRPC.Request
  ): Promise<LeoRPCResult<ValidateCSVAndUnmapRespondentsFromSpocsRPC.Response, ValidateCSVAndUnmapRespondentsFromSpocsRPC.Errors.Errors>>;
}

export namespace ValidateCSVAndUnmapRespondentsFromSpocsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    documentId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isValidateCSVAndUnmapRespondentsFromSpocsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly documentId: LeoUUID;
  
  
    constructor(
    projectId: LeoUUID, 
    documentId: LeoUUID
    ) {
      this.projectId = projectId;
      this.documentId = documentId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const documentId = getLeoUuid(dto, "documentId", InvalidRequestDocumentIdError);
      return new Request(
        projectId,
        documentId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          documentId: this.documentId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `ValidateCSVAndUnmapRespondentsFromSpocsRPC(projectId='${this.projectId}',documentId='${this.documentId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `ValidateCSVAndUnmapRespondentsFromSpocsRPC.Response()`;
  };

  export enum RPCError {
    InvalidFile = "INVALID_FILE",
    UnknownFile = "UNKNOWN_FILE",
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    SpocAssignmentInProgress = "SPOC_ASSIGNMENT_IN_PROGRESS"
  }

  export namespace Errors {
    
    export interface InvalidFileDTO {
      code: string;
      invalidFileDetails: string;
    }
    
    export class InvalidFile extends LeoRPCError<RPCError> {
      readonly isInvalidFile: boolean = true;
    
      readonly invalidFileDetails: string;
    
      constructor(
      invalidFileDetails: string
      ) {
        const code = RPCError.InvalidFile;
        super(code);
        InvalidFile.validateInvalidFileDetails(invalidFileDetails);
        this.invalidFileDetails = invalidFileDetails;
      }
    
      static fromDTO(dto: InvalidFileDTO): InvalidFile {
        const invalidFileDetails = getString(dto, "invalidFileDetails", InvalidResponseInvalidFileInvalidFileDetailsError);
        return new InvalidFile(
          invalidFileDetails
        );
      }
    
      toDTO(): InvalidFileDTO {
        const dto: InvalidFileDTO = {
          code: this.code,
          invalidFileDetails: this.invalidFileDetails
        };
        return dto;
      }
    
      private static validateInvalidFileDetails = (invalidFileDetails: string): void => {
        if(invalidFileDetails.trim().length === 0) {
          throw new InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError(
            `Attribute invalidFileDetails cannot be an empty string.`
          );
        }
      }
      
    }
    
    InvalidFile.prototype.toString = function toString(): string {
      return `ValidateCSVAndUnmapRespondentsFromSpocsRPC.INVALID_FILE(invalidFileDetails='${this.invalidFileDetails}')`;
    };
    
    
    export interface UnknownFileDTO {
      code: string;
    }
    
    export class UnknownFile extends LeoRPCError<RPCError> {
      readonly isUnknownFile: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.UnknownFile;
        super(code);
      }
    
    
      toDTO(): UnknownFileDTO {
        const dto: UnknownFileDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    UnknownFile.prototype.toString = function toString(): string {
      return `ValidateCSVAndUnmapRespondentsFromSpocsRPC.UNKNOWN_FILE()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `ValidateCSVAndUnmapRespondentsFromSpocsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `ValidateCSVAndUnmapRespondentsFromSpocsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface SpocAssignmentInProgressDTO {
      code: string;
    }
    
    export class SpocAssignmentInProgress extends LeoRPCError<RPCError> {
      readonly isSpocAssignmentInProgress: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SpocAssignmentInProgress;
        super(code);
      }
    
    
      toDTO(): SpocAssignmentInProgressDTO {
        const dto: SpocAssignmentInProgressDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SpocAssignmentInProgress.prototype.toString = function toString(): string {
      return `ValidateCSVAndUnmapRespondentsFromSpocsRPC.SPOC_ASSIGNMENT_IN_PROGRESS()`;
    };
    

    export type Errors = InvalidFile | UnknownFile | InvalidProjectId | ProjectAlreadyArchived | SpocAssignmentInProgress;
    export type ErrorsDTO = InvalidFileDTO | UnknownFileDTO | InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | SpocAssignmentInProgressDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidFile:
          return InvalidFile.fromDTO(dto as InvalidFileDTO);
        case RPCError.UnknownFile:
          return new UnknownFile();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.SpocAssignmentInProgress:
          return new SpocAssignmentInProgress();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends ValidateCSVAndUnmapRespondentsFromSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestDocumentIdError extends ValidateCSVAndUnmapRespondentsFromSpocsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestDocumentIdError";
  }
}


export class InvalidResponseInvalidFileError extends ValidateCSVAndUnmapRespondentsFromSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsError extends InvalidResponseInvalidFileError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsError";
  }
}

export class InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError extends InvalidResponseInvalidFileInvalidFileDetailsError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidFileInvalidFileDetailsNonEmptyError";
  }
}
export class InvalidResponseUnknownFileError extends ValidateCSVAndUnmapRespondentsFromSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseUnknownFileError";
  }
}

export class InvalidResponseInvalidProjectIdError extends ValidateCSVAndUnmapRespondentsFromSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends ValidateCSVAndUnmapRespondentsFromSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseSpocAssignmentInProgressError extends ValidateCSVAndUnmapRespondentsFromSpocsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSpocAssignmentInProgressError";
  }
}

